// Gera texto e FAQs do bloco SEO a partir de stats reais. Sem números reais
// nada é afirmado — evita texto artificial e doorway pages.

import type { SeoEntry } from "./seo-config";
import type { SeoStats } from "./seo-format";
import { fmtPrice } from "./seo-format";

export function buildIntro(entry: SeoEntry, stats: SeoStats): string {
  if (entry.location) return buildIntroEstado(entry, stats);

  const parts: string[] = [];

  const opening = entry.shortDescription
    ? `${entry.displayName} — ${entry.shortDescription}`
    : `${entry.displayName} é um dos modelos que mais aparece em revendas de blindados no Brasil.`;
  parts.push(opening);

  const range: string[] = [];
  if (stats.priceMin != null && stats.priceMax != null) {
    range.push(
      `No momento monitoramos ${stats.total} ofertas, com preços entre ${fmtPrice(stats.priceMin)} e ${fmtPrice(stats.priceMax)}` +
        (stats.priceMedian != null ? ` (mediana ${fmtPrice(stats.priceMedian)})` : "") +
        ".",
    );
  }
  if (stats.yearMin != null && stats.yearMax != null) {
    range.push(
      `Os anos vão de ${stats.yearMin} a ${stats.yearMax}` +
        (stats.yearMode != null ? `, sendo ${stats.yearMode} o mais comum` : "") +
        ".",
    );
  }
  if (range.length) parts.push(range.join(" "));

  parts.push(
    "Antes de fechar negócio, confirme nível de blindagem (geralmente IIIA), procedência da blindadora, laudo do Exército, histórico de manutenção e custo de seguro — esses fatores podem variar bastante entre carros aparentemente iguais.",
  );

  return parts.join("\n\n");
}

function buildIntroEstado(entry: SeoEntry, stats: SeoStats): string {
  const parts: string[] = [];

  const opening = entry.shortDescription
    ? `${entry.displayName} — ${entry.shortDescription}`
    : `${entry.displayName} tem mercado ativo de carros blindados usados, com revendas especializadas e boa oferta de modelos premium.`;
  parts.push(opening);

  const range: string[] = [];
  if (stats.priceMin != null && stats.priceMax != null) {
    range.push(
      `No momento monitoramos ${stats.total} ofertas em ${entry.displayName}, com preços entre ${fmtPrice(stats.priceMin)} e ${fmtPrice(stats.priceMax)}` +
        (stats.priceMedian != null ? ` (mediana ${fmtPrice(stats.priceMedian)})` : "") +
        ".",
    );
  }
  if (stats.yearMin != null && stats.yearMax != null) {
    range.push(
      `Os anos vão de ${stats.yearMin} a ${stats.yearMax}` +
        (stats.yearMode != null ? `, sendo ${stats.yearMode} o mais comum` : "") +
        ".",
    );
  }
  if (range.length) parts.push(range.join(" "));

  parts.push(
    "Antes de fechar negócio, confirme nível de blindagem (geralmente IIIA), procedência da blindadora, laudo do Exército, histórico de manutenção e custo de seguro — esses fatores podem variar bastante entre carros aparentemente iguais.",
  );

  return parts.join("\n\n");
}

export function buildFaqs(entry: SeoEntry, stats: SeoStats) {
  if (entry.location) return buildFaqsEstado(entry, stats);

  const faqs: { q: string; a: string }[] = [];

  if (stats.priceMin != null && stats.priceMax != null) {
    faqs.push({
      q: `Qual o preço de ${entry.displayName} blindada hoje?`,
      a:
        `Hoje as ofertas variam de ${fmtPrice(stats.priceMin)} a ${fmtPrice(stats.priceMax)}` +
        (stats.priceMedian != null
          ? `, com mediana em torno de ${fmtPrice(stats.priceMedian)}`
          : "") +
        ". Os valores mudam conforme ano, KM, blindadora e nível de proteção.",
    });
  }

  if (stats.yearMin != null && stats.yearMax != null) {
    faqs.push({
      q: `De quais anos aparecem mais ${entry.displayName} blindadas?`,
      a:
        `Encontramos ofertas de ${stats.yearMin} a ${stats.yearMax}` +
        (stats.yearMode != null ? `, com concentração em ${stats.yearMode}` : "") +
        ". Modelos mais novos costumam ter preço maior, garantia de fábrica e revenda mais fácil.",
    });
  }

  faqs.push({
    q: `Onde aparecem mais ${entry.displayName} blindadas?`,
    a: "Monitoramos mais de 60 sites especializados em blindados e agregadores de anúncios. Assim que um anúncio novo é publicado, ele aparece aqui — sem precisar ficar checando vários sites.",
  });

  faqs.push({
    q: `Vale a pena comprar ${entry.displayName} blindada usada?`,
    a: "Depende do uso e do orçamento. Carros usados e blindados têm depreciação menor que zero-quilômetro novos blindados, mas exigem inspeção da estrutura, vidros e laudo da blindadora. Sempre solicite o laudo do Exército antes de fechar.",
  });

  faqs.push({
    q: "Como funciona o alerta do Busca Blindados?",
    a: `Você define o filtro (modelo, ano, KM, faixa de preço) e te avisamos por email assim que uma nova ${entry.displayName} blindada bater nos critérios em qualquer uma das fontes que monitoramos.`,
  });

  return faqs;
}

export function buildIntroModeloEstado(
  entry: SeoEntry,
  stats: SeoStats,
  locationName: string,
): string {
  const parts: string[] = [];

  const opening = entry.shortDescription
    ? `${entry.displayName} — ${entry.shortDescription}`
    : `${entry.displayName} é um dos modelos mais procurados no mercado de blindados de ${locationName}.`;
  parts.push(opening);

  const range: string[] = [];
  if (stats.priceMin != null && stats.priceMax != null) {
    range.push(
      `No momento monitoramos ${stats.total} ofertas de ${entry.displayName} blindada em ${locationName}, com preços entre ${fmtPrice(stats.priceMin)} e ${fmtPrice(stats.priceMax)}` +
        (stats.priceMedian != null ? ` (mediana ${fmtPrice(stats.priceMedian)})` : "") +
        ".",
    );
  }
  if (stats.yearMin != null && stats.yearMax != null) {
    range.push(
      `Os anos vão de ${stats.yearMin} a ${stats.yearMax}` +
        (stats.yearMode != null ? `, sendo ${stats.yearMode} o mais comum` : "") +
        ".",
    );
  }
  if (range.length) parts.push(range.join(" "));

  parts.push(
    "Antes de fechar negócio, confirme nível de blindagem (geralmente IIIA), procedência da blindadora, laudo do Exército, histórico de manutenção e custo de seguro — esses fatores podem variar bastante entre carros aparentemente iguais.",
  );

  return parts.join("\n\n");
}

export function buildFaqsModeloEstado(
  entry: SeoEntry,
  stats: SeoStats,
  locationName: string,
) {
  const faqs: { q: string; a: string }[] = [];

  faqs.push({
    q: `Tem ${entry.displayName} blindada em ${locationName}?`,
    a:
      stats.total > 0
        ? `Sim. No momento monitoramos ${stats.total} ${stats.total === 1 ? "oferta" : "ofertas"} de ${entry.displayName} blindada em ${locationName}` +
          (stats.priceMin != null ? `, a partir de ${fmtPrice(stats.priceMin)}` : "") +
          ". Novas ofertas aparecem assim que são publicadas nas revendas que monitoramos."
        : `No momento não encontramos ofertas de ${entry.displayName} blindada em ${locationName}, mas monitoramos as principais revendas continuamente. Configure um alerta para ser avisado assim que aparecer.`,
  });

  if (stats.priceMin != null && stats.priceMax != null) {
    faqs.push({
      q: `Qual o preço médio de ${entry.displayName} blindada em ${locationName}?`,
      a:
        `As ofertas de ${entry.displayName} blindada em ${locationName} variam de ${fmtPrice(stats.priceMin)} a ${fmtPrice(stats.priceMax)}` +
        (stats.priceMedian != null ? `, com mediana em torno de ${fmtPrice(stats.priceMedian)}` : "") +
        ". Os valores mudam conforme ano, KM, blindadora e nível de proteção.",
    });
  }

  if (stats.yearMin != null && stats.yearMax != null) {
    faqs.push({
      q: `De quais anos aparecem mais ${entry.displayName} blindadas em ${locationName}?`,
      a:
        `Encontramos ofertas de ${stats.yearMin} a ${stats.yearMax}` +
        (stats.yearMode != null ? `, com concentração em ${stats.yearMode}` : "") +
        ". Modelos mais novos costumam ter preço maior, garantia de fábrica e revenda mais fácil.",
    });
  }

  faqs.push({
    q: `Vale a pena comprar ${entry.displayName} blindada usada em ${locationName}?`,
    a: "Depende do uso e do orçamento. Comprar no mercado local facilita vistoria presencial, negociação e transferência. Sempre solicite o laudo do Exército, verifique procedência da blindadora e faça inspeção dos vidros antes de fechar.",
  });

  faqs.push({
    q: "Como funciona o alerta do Busca Blindados?",
    a: `Você define o filtro (modelo, estado, ano, KM, faixa de preço) e te avisamos por email assim que uma nova ${entry.displayName} blindada aparecer em ${locationName} em qualquer uma das fontes que monitoramos.`,
  });

  return faqs;
}

function buildFaqsEstado(entry: SeoEntry, stats: SeoStats) {
  const faqs: { q: string; a: string }[] = [];

  if (stats.priceMin != null && stats.priceMax != null) {
    faqs.push({
      q: `Qual o preço de carros blindados em ${entry.displayName}?`,
      a:
        `As ofertas em ${entry.displayName} variam de ${fmtPrice(stats.priceMin)} a ${fmtPrice(stats.priceMax)}` +
        (stats.priceMedian != null ? `, com mediana em torno de ${fmtPrice(stats.priceMedian)}` : "") +
        ". Os valores mudam conforme modelo, ano, KM e blindadora.",
    });
  }

  if (stats.yearMin != null && stats.yearMax != null) {
    faqs.push({
      q: `Quais anos de blindados aparecem mais em ${entry.displayName}?`,
      a:
        `Encontramos ofertas de ${stats.yearMin} a ${stats.yearMax}` +
        (stats.yearMode != null ? `, com concentração em ${stats.yearMode}` : "") +
        ". Modelos mais novos costumam ter preço maior, mas melhor garantia e revenda.",
    });
  }

  faqs.push({
    q: `Onde comprar carro blindado ${entry.displayName}?`,
    a: "Monitoramos mais de 60 sites especializados em blindados e agregadores de anúncios. Assim que um anúncio novo é publicado, ele aparece aqui — sem precisar ficar checando vários sites.",
  });

  faqs.push({
    q: `Vale a pena comprar blindado usado em ${entry.displayName}?`,
    a: "Depende do uso e do orçamento. O mercado local é importante: facilita vistoria presencial, negociação e transferência. Sempre solicite laudo do Exército, verifique procedência da blindadora e faça inspeção dos vidros antes de fechar.",
  });

  faqs.push({
    q: "Como funciona o alerta do Busca Blindados?",
    a: `Você define o filtro (estado, modelo, ano, KM, faixa de preço) e te avisamos por email assim que uma nova oferta bater nos critérios em qualquer uma das fontes que monitoramos em ${entry.displayName}.`,
  });

  return faqs;
}
